import { supabase } from "@/lib/supabase";
import type { CfaoVehicle, VehicleFeature, VehicleStatus } from "@/data/cfaoVehicles";

type ImageRow = {
  id: string;
  url: string;
  storage_path: string | null;
  alt_text: string;
  sort_order: number;
};

type VehicleRow = Record<string, any> & { vehicle_images?: ImageRow[] };

const mapVehicle = (row: VehicleRow): CfaoVehicle => {
  const imageRows = [...(row.vehicle_images || [])].sort((a, b) => a.sort_order - b.sort_order);
  const images = imageRows.map((image) => image.url).filter(Boolean);
  const features = Array.isArray(row.features) ? row.features : [];
  const specifications = row.specifications && typeof row.specifications === "object" ? row.specifications : {};
  const primaryImage = images[0] || "";

  return {
    databaseId: row.id,
    id: row.stock_number,
    stockNumber: row.stock_number,
    slug: row.slug,
    year: row.year,
    make: row.make,
    fullTitle: row.full_title,
    model: row.model,
    variant: row.variant,
    price: row.price,
    previousPrice: row.previous_price,
    priceDropAmount: row.price_drop_amount || 0,
    priceDroppedAt: row.price_dropped_at,
    monthlyPayment: row.monthly_payment === null ? null : Number(row.monthly_payment),
    mileage: row.mileage,
    fuelType: row.fuel_type,
    transmission: row.transmission,
    bodyType: row.body_type,
    color: row.color,
    driveType: row.drive_type,
    seats: row.seats || 0,
    engineSize: row.engine_size,
    condition: row.condition,
    description: row.description,
    serviceHistory: row.service_history,
    location: row.location,
    rating: row.rating === null ? null : Number(row.rating),
    image: primaryImage,
    images,
    url: row.source_url || `/vehicles/${row.slug}/`,
    badge: row.price_drop_amount > 0 ? "Price Drop" : row.badge,
    sourceOrder: row.source_order,
    status: row.status,
    featured: row.featured,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    detail: {
      image: primaryImage,
      additionalImages: images.slice(1),
      features,
      specifications,
      engineSize: row.engine_size,
      condition: row.condition,
      variant: row.variant,
      description: row.description,
      serviceHistory: row.service_history,
    },
  };
};

const vehicleSelect = "*, vehicle_images(id, url, storage_path, alt_text, sort_order)";

export async function getVehicles(): Promise<CfaoVehicle[]> {
  const { data, error } = await supabase
    .from("vehicles")
    .select(vehicleSelect)
    .order("source_order", { ascending: true });
  if (error) throw error;
  return (data || []).map(mapVehicle);
}

export async function getVehicleBySlug(slug: string): Promise<CfaoVehicle | null> {
  const { data, error } = await supabase
    .from("vehicles")
    .select(vehicleSelect)
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return data ? mapVehicle(data) : null;
}

export async function getVehicleById(id: string): Promise<CfaoVehicle | null> {
  const { data, error } = await supabase
    .from("vehicles")
    .select(vehicleSelect)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data ? mapVehicle(data) : null;
}

export type VehicleInput = {
  stockNumber: string;
  slug?: string;
  year: number;
  make: string;
  fullTitle: string;
  model: string;
  variant: string;
  price: number;
  previousPrice?: number | null;
  monthlyPayment?: number | null;
  mileage: number;
  fuelType: string;
  transmission: string;
  bodyType: string;
  color: string;
  driveType: string;
  seats: number | null;
  engineSize: string;
  condition: string;
  description: string;
  serviceHistory?: string | null;
  location: string;
  rating?: number | null;
  sourceUrl?: string | null;
  sourceOrder?: number;
  features: VehicleFeature[];
  specifications: Record<string, Record<string, string>>;
  status: VehicleStatus;
  featured: boolean;
  images: string[];
};

const slugify = (value: string) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

export async function saveVehicle(input: VehicleInput, id?: string): Promise<string> {
  const payload: Record<string, any> = {
    stock_number: input.stockNumber.trim(),
    slug: input.slug?.trim() || `${slugify(input.fullTitle)}-${slugify(input.stockNumber)}`,
    year: input.year,
    make: input.make.trim(),
    full_title: input.fullTitle.trim(),
    model: input.model.trim(),
    variant: input.variant.trim(),
    price: input.price,
    monthly_payment: input.monthlyPayment ?? null,
    mileage: input.mileage,
    fuel_type: input.fuelType.trim(),
    transmission: input.transmission.trim(),
    body_type: input.bodyType.trim(),
    color: input.color.trim(),
    drive_type: input.driveType.trim(),
    seats: input.seats,
    engine_size: input.engineSize.trim(),
    condition: input.condition.trim(),
    description: input.description.trim(),
    service_history: input.serviceHistory?.trim() || null,
    location: input.location.trim(),
    rating: input.rating ?? null,
    source_url: input.sourceUrl || null,
    source_order: input.sourceOrder ?? 0,
    features: input.features,
    specifications: input.specifications,
    status: input.status,
    featured: input.status === "sold" ? false : input.featured,
  };
  if (!id || input.previousPrice !== undefined) payload.previous_price = input.previousPrice ?? null;

  const query = id
    ? supabase.from("vehicles").update(payload).eq("id", id).select("id").single()
    : supabase.from("vehicles").insert(payload).select("id").single();
  const { data, error } = await query;
  if (error) throw error;
  const vehicleId = data.id as string;

  const { error: deleteError } = await supabase.from("vehicle_images").delete().eq("vehicle_id", vehicleId);
  if (deleteError) throw deleteError;
  if (input.images.length) {
    const { error: imageError } = await supabase.from("vehicle_images").insert(
      input.images.map((url, sortOrder) => ({
        vehicle_id: vehicleId,
        url,
        alt_text: `${input.fullTitle} - image ${sortOrder + 1}`,
        sort_order: sortOrder,
      }))
    );
    if (imageError) throw imageError;
  }
  return vehicleId;
}

export async function updateVehicleStatus(id: string, status: VehicleStatus) {
  const { error } = await supabase.from("vehicles").update({ status }).eq("id", id);
  if (error) throw error;
}

export async function updateVehicleFeatured(id: string, featured: boolean) {
  const { error } = await supabase.from("vehicles").update({ featured }).eq("id", id);
  if (error) throw error;
}

export async function deleteVehicle(id: string) {
  const { error } = await supabase.from("vehicles").delete().eq("id", id);
  if (error) throw error;
}

export async function uploadVehicleImage(file: File): Promise<string> {
  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const path = `${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.${extension}`;
  const { error } = await supabase.storage.from("vehicle-images").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (error) throw error;
  return supabase.storage.from("vehicle-images").getPublicUrl(path).data.publicUrl;
}
